"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface AnimatedCounterProps {
  value: number;
  duration?: number;
  suffix?: string;
}
function AnimatedCounter({
  value,
  duration = 2000,
  suffix = ""
}: AnimatedCounterProps) {
  const [count, setCount] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLSpanElement>();
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number | null = null;
    let animationFrameId: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * value));
      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      }
    };
    animationFrameId = window.requestAnimationFrame(step);
    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isVisible, value, duration]);
  const formattedCount = new Intl.NumberFormat("en-US").format(count);
  return <span ref={ref}>
      {formattedCount}
      {suffix}
    </span>;
}
const statsData = [{
  id: "accuracy",
  value: 99.9,
  label: "Delivery Accuracy",
  suffix: "%"
}, {
  id: "defect",
  value: 0.00,
  label: "Defect Rate",
  suffix: "%"
}, {
  id: "logistics",
  value: 24,
  label: "Global Logistics",
  suffix: "/7"
}];
export default function GlobalImpactInNumbers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222968" id="quality-assurance-metrics" className="dark bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 opacity-10 pointer-events-none" style={{
      backgroundImage: `
            linear-gradient(to right, hsl(var(--muted-foreground)) 1px, transparent 1px),
            linear-gradient(to bottom, hsl(var(--muted-foreground)) 1px, transparent 1px)
          `,
      backgroundSize: "30px 30px"
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,hsl(var(--background))_90%)] pointer-events-none" />

      <div className="max-w-[1200px] mx-auto px-6 relative z-10">
        <div ref={ref} className={`flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {statsData.map((stat, i) => <React.Fragment key={stat.id}>
              {}
              <div data-index={i} className="flex flex-col items-center justify-center text-center flex-1 w-full">
                <div className="font-default text-6xl md:text-7xl lg:text-8xl font-semibold text-primary tracking-tighter drop-shadow-md">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} duration={2500 + i * 200} />
                </div>
                <div className="mt-6 font-mono text-xs md:text-sm text-secondary uppercase tracking-[0.2em] font-bold">
                  {stat.label}
                </div>
                {}
                <div className="mt-4 w-32 h-0.5 bg-muted overflow-hidden rounded-md">
                  <div className={`h-full bg-secondary transition-all duration-[3000ms] ease-in-out ${isVisible ? "w-full" : "w-0"}`} style={{
                boxShadow: '0 0 10px hsl(var(--secondary))'
              }} />
                </div>
              </div>

              {}
              {i < statsData.length - 1 && <div className="hidden lg:block w-px h-32 bg-border shrink-0" aria-hidden="true" />}
              
              {}
              {i < statsData.length - 1 && <div className="block lg:hidden h-px w-32 bg-border shrink-0" aria-hidden="true" />}
            </React.Fragment>)}
        </div>
      </div>
    </section>;
}